import * as O from 'fp-ts/Option'
import { pipe } from 'fp-ts/function'
import type { Observable, Subject, Subscription } from 'rxjs'
import { map, startWith, take } from 'rxjs/operators'
import type { FormWorker } from '../FormWorker'
import type { FieldsState } from '../formBuilder'
import type { Employe } from './Employe'

export namespace EmployeSubscriber {
    export type runType = 'init' | 'onChange' | 'onUpdate'

    export interface EmployeInitializer {
        name: string
        config: Employe.EmployeBuilded<FieldsState<string>, any, any, any>
        slave: FormWorker.employeSlave
        updaterSubject: Subject<any>
        values: FieldsState<string>
        unitsForm: FormWorker.Props['unitsForm']
        runType: runType
        dispatch: FormWorker.employeesStatusOnNext
    }
}

export class EmployeSubscriber {
    public employeSubscriptions: { [name: string]: O.Option<Subscription> } = {}

    private doSubscribe(initializer: EmployeSubscriber.EmployeInitializer) {
        return pipe(
            initializer.config.employe(initializer.values, initializer.runType),
            O.map((employe: Observable<unknown>) =>
                employe
                    .pipe(
                        take(1),
                        map(r => ({ status: 'done' as const, data: r })),
                        startWith({ status: 'pending' as const, data: null })
                    )
                    .subscribe(
                        n => {
                            initializer.dispatch({ type: n.status, name: initializer.name })
                            initializer.slave.onNext(
                                initializer.runType,
                                n,
                                initializer.updaterSubject,
                                initializer.unitsForm,
                                initializer.values
                            )
                        },
                        (e: unknown) => {
                            initializer.dispatch({ type: 'error', name: initializer.name })
                            initializer.slave.onError &&
                                initializer.slave.onError(
                                    initializer.runType,
                                    e,
                                    initializer.updaterSubject,
                                    initializer.unitsForm,
                                    initializer.values
                                )
                        }
                    )
            )
        )
    }

    public subscribeEmploye(initializers: EmployeSubscriber.EmployeInitializer[]) {
        initializers.map(initializer => {
            const subscriptionOp = this.employeSubscriptions[initializer.name] || O.none

            if (
                pipe(
                    subscriptionOp,
                    O.filter(s => !s.closed && initializer.runType === 'init'),
                    O.isSome
                )
            ) {
                return
            }

            this.employeSubscriptions[initializer.name] = pipe(subscriptionOp, O.isSome)
                ? pipe(
                      subscriptionOp,
                      O.map(s => !s.closed && s.unsubscribe()),
                      O.chain(_ => this.doSubscribe(initializer))
                  )
                : this.doSubscribe(initializer)
        })
    }

    public unscribeAll() {
        Object.values(this.employeSubscriptions).map(
            subscriptionOp =>
                subscriptionOp &&
                pipe(
                    subscriptionOp,
                    O.map(s => s.closed && s.unsubscribe())
                )
        )
    }
}
